import { Nullable } from '@ps/utils';
import {
  InsightsChildState,
  InsightsState,
} from '../survey-creation-insights-state.model';
import { DEFAULT_CHILD_INSIGHTS_STATE } from '../survey-creation-insights-state.constants';
import { DEFAULT_SURVEY_CHILD_KEY } from '../../survey-creation-state.constants';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import {
  CHILD_SCOPE,
  ChildMapPatch,
  patchChildrenMap,
  resolveActiveChildKey as resolveActiveChildKeyOf,
  SurveyChildScope,
} from '../../survey-creation-child-registry.helper';

export type InsightsChildPatch = ChildMapPatch<InsightsChildState>;

export function isLoadedInsightsSibling(
  children: Record<SurveyChildKey, InsightsChildState>,
  surveyId: number,
): boolean {
  return !!children[surveyId] && Object.keys(children).length > 1;
}

/**
 * A fresh insights state targeting `rootSurveyId` — mirrors
 * `initializeCoreState`: the one child is keyed by the root id directly in
 * edit mode, else the empty-string pre-draft placeholder.
 */
export function initializeInsightsState(
  rootSurveyId: Nullable<number>,
): InsightsState {
  const childKey = rootSurveyId ?? DEFAULT_SURVEY_CHILD_KEY;

  return {
    children: {
      [childKey]: DEFAULT_CHILD_INSIGHTS_STATE,
    },
  };
}

export function resolveActiveInsightsChildKey(
  state: InsightsState,
  activeChildKey: Nullable<SurveyChildKey>,
): SurveyChildKey {
  return resolveActiveChildKeyOf(
    state.children,
    activeChildKey,
    DEFAULT_SURVEY_CHILD_KEY,
  );
}

export function selectActiveInsightsChild(
  state: InsightsState,
  activeChildKey: Nullable<SurveyChildKey>,
): InsightsChildState {
  return (
    state.children[resolveActiveInsightsChildKey(state, activeChildKey)] ??
    DEFAULT_CHILD_INSIGHTS_STATE
  );
}

export function patchActiveInsightsChild(
  state: InsightsState,
  activeChildKey: Nullable<SurveyChildKey>,
  scope: SurveyChildScope,
  patch: InsightsChildPatch,
): InsightsState {
  return {
    ...state,
    children: patchChildrenMap(
      state.children,
      activeChildKey,
      scope,
      patch,
      DEFAULT_CHILD_INSIGHTS_STATE,
    ),
  };
}

export function patchInsightsChild(
  state: InsightsState,
  key: SurveyChildKey,
  patch: InsightsChildPatch,
): InsightsState {
  return {
    ...state,
    children: patchChildrenMap(
      state.children,
      null,
      CHILD_SCOPE.keys([key]),
      patch,
      DEFAULT_CHILD_INSIGHTS_STATE,
    ),
  };
}
